// React hooks
import { createContext, useContext, useEffect } from "react";
// Skybase Hooks
import { API_BASE_URL } from "../config";
// External hooks/components
import { useAuth as useClerkAuth } from "@clerk/react";


// Create React Context
const BillingContext = createContext();


// ===============
// BillingProvider – provides GC related to payment processing related data.
// ===============
export default function BillingProvider({ children }) {

    // init hooks
    const { getToken } = useClerkAuth();

    // ======================
    // Start Checkout Session – creates a unique Stripe Checkout Session URL (String) using the input `stripe_price_lookup_key` for an authenticated Clerk CurrentUser.
    // ======================
    async function startCheckoutSession(stripe_price_lookup_key) {
        
        // Protected API Endpoint: Never fetch() without <Clerk JWT>
        const token = await getToken();
        if (!token) { return };

        // `POST /billing/checkout/${stripe_price_lookup_key}`
        const url = new URL(`${API_BASE_URL}/api/v1/billing/checkout/${stripe_price_lookup_key}`);
        const res = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });

        // Error handling
        if (!res.ok) { throw new Error(`SKYBASE (Client): HTTP ${res.status} Error – Unable to start Stripe Checkout Session.`)};
        
        // Handle JSON response data
        const data = await res.json();

        // ⚠️ Developer debugging
        // console.log(`SKYBASE (client): Stripe Checkout Session URL\n\n${data.stripe_checkout_url}`);

        // return the URL
        return data.stripe_checkout_url;
    }

    
    // ============================
    // Start Billing Portal Session – creates a unique Stripe Billing Portal Session URL (String) for an authenticated Clerk CurrentUser.
    // ============================
    async function startBillingPortalSession() {
        
        // Protected API Endpoint: Never fetch() without <Clerk JWT>
        const token = await getToken();
        if (!token) { return };

        // `POST /billing/portal`
        const url = new URL(`${API_BASE_URL}/api/v1/billing/billing-portal`);
        const res = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });

        // Error handling
        if (!res.ok) { throw new Error(`SKYBASE (Client): HTTP ${res.status} Error – Unable to start Stripe BillingPortalSession.`)};
        
        // Handle JSON response data
        const data = await res.json();
        
        // ⚠️ Developer debugging
        // console.log(`SKYBASE (client): Stripe Checkout Session URL\n\n${data.stripe_billing_portal_url}`);

        // return the URL
        return data.stripe_billing_portal_url;
    }


    
    // JSX Component – Provider
    return(
        <BillingContext.Provider
            value={{
                startCheckoutSession,           // async (String)
                startBillingPortalSession,      // async ()
            }}
        >
            { children }
        </BillingContext.Provider>
    )
}



// useBilling Hook
// ===============
export function useBilling() {
    return useContext(BillingContext);
}